import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import classNames from 'classnames';
import { user } from '../../../redux/user';
import { useFlexLocationsQuery } from '../../../redux/flex-locations';
import utils from '../../../utils/utils';
import Permissions from '../../../constants/Permissions';
import manageAccountIcon from '../../../assets/images/manage-accounts.svg';
import {
	updateLocationsViewStateForManagementCompany,
	viewStateForWorkforceLocations
} from '../../../redux/flexWorkforceView';

const DEFAULT_SORT = { field: 'facility_name', order: 1 };

const FlexLocations = ({ managementCompany, dataTableRef, exportFilename, filters }) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);

	const { data: rawLocations, isLoading } = useFlexLocationsQuery(
		{ managementCompanyId: managementCompany.id },
		{
			refetchOnMountOrArgChange: true
		}
	);

	const locations = useMemo(
		() =>
			rawLocations?.map((location) => ({
				...location,
				fullAddress: `${location.address}, ${location.city}, ${location.state} ${location.zip_code}`,
				formatedPhoneNumber: utils.formatPhoneNumber(location.phone_number),
				professionalsCount: `${location.enabled_flex_workers_count} of ${location.total_flex_workers_count}`,
				allowClaim: location.allow_claim ? 'Yes' : 'No'
			})),
		[rawLocations]
	);

	const [filteredLocationsCount, setFilteredLocationsCount] = useState(null);
	const viewState = useSelector(viewStateForWorkforceLocations(managementCompany.id));
	const sort = viewState?.sort || DEFAULT_SORT;
	const persistedScrollTop = viewState?.scrollTop;

	const onSort = (e) => {
		dispatch(
			updateLocationsViewStateForManagementCompany({
				managementCompanyId: managementCompany.id,
				sort: { field: e.sortField, order: e.sortOrder }
			})
		);
	};
	const onFilter = (e) => setFilteredLocationsCount(e.length);

	useEffect(() => {
		//	lastLocationsRef.current = null;
		setFilteredLocationsCount(null);
	}, [managementCompany.id]);

	const _filteredLocationsCount = filteredLocationsCount !== null ? filteredLocationsCount : locations?.length;

	const restoredForMcRef = useRef(null);
	const latestScrollTopRef = useRef(null);
	const scrollTopMcIdRef = useRef(managementCompany.id);

	// Persist the scroll position to redux when the user leaves this MC's view — either by
	// switching management companies (detected via previous-id ref) or by unmounting the
	// datatable (cleanup runs on unmount). scrollTopMcIdRef always holds the mcId that
	// latestScrollTopRef's value belongs to.
	useEffect(() => {
		if (scrollTopMcIdRef.current !== managementCompany.id && latestScrollTopRef.current != null) {
			dispatch(
				updateLocationsViewStateForManagementCompany({
					managementCompanyId: scrollTopMcIdRef.current,
					scrollTop: latestScrollTopRef.current
				})
			);
			latestScrollTopRef.current = null;
		}
		scrollTopMcIdRef.current = managementCompany.id;
		return () => {
			if (latestScrollTopRef.current == null) return;
			dispatch(
				updateLocationsViewStateForManagementCompany({
					managementCompanyId: scrollTopMcIdRef.current,
					scrollTop: latestScrollTopRef.current
				})
			);
			latestScrollTopRef.current = null;
		};
	}, [managementCompany.id, dispatch]);

	useEffect(() => {
		if (restoredForMcRef.current === managementCompany.id) return undefined;
		if (isLoading || locations?.length === 0) return undefined;
		restoredForMcRef.current = managementCompany.id;
		if (!persistedScrollTop) return undefined;
		// only try to scroll on the next paint cycle, when we know the datatable has been already rendered
		const rafId = requestAnimationFrame(() => {
			dataTableRef.current?.getVirtualScroller()?.scrollTo({ top: persistedScrollTop, behavior: 'auto' });
		});
		return () => cancelAnimationFrame(rafId);
	}, [isLoading, locations?.length, managementCompany.id, persistedScrollTop, dataTableRef]);

	const addressBodyTemplate = (data) => (
		<>
			{data.address}, {data.city}, {data.state} <span className="zip-code">{data.zip_code}</span>
		</>
	);
	const allowClaimTemplate = (data) => (data.allow_claim ? 'Yes' : <span className="text-warning-500">No</span>);

	const exportFunction = ({ rowData, data, column }) => {
		if (column.props.header === 'Allow app claiming') {
			return rowData.allow_claim ? 'Yes' : 'No';
		}
		return data;
	};
	const actionsTemplate = (item) => {
		if (
			userData.permissionsByFacility[item.facility_id] &&
			utils.isAuthorizedExact(Permissions.INTERNAL_STAFF, userData.permissionsByFacility[item.facility_id])
		) {
			return (
				<div className="action-menu-container">
					<OverlayTrigger
						placement="bottom"
						overlay={
							<Tooltip className="workforce-action-tooltip" id="manage-location">
								Manage location
							</Tooltip>
						}
					>
						<button
							type="button"
							className="btn"
							onClick={() =>
								history.push(`/private/workforce/${managementCompany.id}/facilities/${item.facility_id}/professionals`)
							}
							aria-label="Manage location"
						>
							<img src={manageAccountIcon} alt="Manage location" />
						</button>
					</OverlayTrigger>
				</div>
			);
		}
		return '';
	};

	return (
		<div className="flex-locations">
			<div className="toolbar">
				<p className="total-count">
					{_filteredLocationsCount} {_filteredLocationsCount > 1 ? 'Locations' : 'Location'}
				</p>
				<div className="flex-one" />
			</div>
			<DataTable
				value={locations}
				ref={dataTableRef}
				filters={filters}
				scrollHeight="flex"
				removableSort
				sortField={sort.field}
				sortOrder={sort.order}
				onSort={onSort}
				size="small"
				stripedRows
				loading={isLoading}
				dataKey="facility_id"
				exportFunction={exportFunction}
				exportFilename={exportFilename}
				onValueChange={onFilter}
				virtualScrollerOptions={{
					itemSize: 65,
					onScroll: (e) => {
						// store the current position in a ref on every scroll
						// we don't need it in redux until the user goes away from the page
						if (restoredForMcRef.current !== managementCompany.id) return;
						latestScrollTopRef.current = e.target.scrollTop;
					}
				}}
				globalFilterFields={['facility_name', 'fullAddress', 'formatedPhoneNumber', 'professionalsCount', 'allowClaim']}
				emptyMessage={
					_filteredLocationsCount ? '' : 'No locations found. Clear or refine your search to see locations.'
				}
			>
				<Column field="facility_name" header="Facility" style={{ width: '25%' }} sortable />
				<Column
					field="fullAddress"
					sortField="zip_code"
					header="Address"
					style={{ width: '35%' }}
					sortable
					body={addressBodyTemplate}
				/>
				<Column field="formatedPhoneNumber" header="Phone" style={{ width: '10%', minWidth: '115px' }} sortable />
				<Column
					header="Professionals"
					style={{ width: '10%' }}
					field="professionalsCount"
					sortable
					sortField="enabled_flex_workers_count"
				/>
				<Column
					header="Allow app claiming"
					field="allowClaim"
					style={{ width: '12%' }}
					sortable
					body={allowClaimTemplate}
					className="status"
				/>
				<Column
					field="actions"
					header="Actions"
					style={{ maxWidth: '80px', width: '8%' }}
					headerClassName={classNames({ 'disabled-column-header': false })}
					body={actionsTemplate}
					exportable={false}
				/>
			</DataTable>
		</div>
	);
};

export default memo(FlexLocations);
